import { useEffect, useState, useRef, useCallback } from "react";
import { Box, Typography } from "@mui/joy";
import { debounce } from "lodash";

import { useInspectionAttributes } from "../../../../hooks/query";
import { useResponsive } from "../../../../hooks";

import { standardVisualMeasurement, Measurement, IVisualMeasurement } from "./types";
import { DesktopView } from "./desktop";
import { MobileView } from "./mobile";
import { AdditionalAttributes } from "./AdditionalAttributes";

export function VisualMeasurement(props: IVisualMeasurement) {
    const { measurements, setMeasurements, attributes, setAttributes } = props;
    const [measurementData, setMeasurementData] = useState<any[]>([]);
    const { isMobile } = useResponsive();

    const { useUpdateSingleAttribute } = useInspectionAttributes();
    const updateMeasurementSvc = useUpdateSingleAttribute();

    // -- Measurement Updates Logic --
    const debouncedApiUpdate = useRef(
        debounce(async (recordId: string, newValue: number) => {
            try {
                const params = { Id: recordId, Qty: Number(newValue) };
                await updateMeasurementSvc.mutateAsync(params);
                setMeasurements((prev: Measurement[]) =>
                    prev.map((m) => (m.Id === recordId ? { ...m, ActualValue: Number(newValue) } : m)),
                );
            } catch (error) {
                console.error(error);
            }
        }, 500),
    ).current;

    const handleMeasurementUpdate = async (eventType: "remove" | "add", event: any) => {
        const delta = eventType === "add" ? 1 : -1;
        setMeasurementData((prev) =>
            prev.map((item) => {
                if (item.name === event.name) {
                    const updatedActual = Math.max(0, item.actual + delta);
                    debouncedApiUpdate(item.id, updatedActual);
                    return { ...item, actual: updatedActual };
                }
                return item;
            }),
        );
    };

    const processData = useCallback(() => {
        if (!measurements || measurements.length === 0) return;
        const newData = standardVisualMeasurement.map((item: any) => {
            const entry = measurements.find((entry: any) => entry.MeasurementType === item);
            if (!entry) return { name: item, total: null, actual: null, id: null };
            return {
                ...item,
                name: entry.MeasurementType,
                total: Number(entry.CalculatedValue),
                actual: Number(entry.ActualValue),
                id: entry.Id,
            };
        });
        setMeasurementData(newData);
    }, [measurements]);

    useEffect(() => {
        processData();
        return () => {
            debouncedApiUpdate.cancel();
        };
    }, [processData, debouncedApiUpdate]);

    return (
        <Box sx={{}}>
            <Typography level="body-sm" sx={{ fontWeight: "md", mb: 1 }}>
                Visual Measurements
            </Typography>

            {isMobile ? (
                <MobileView 
                    measurementData={measurementData} 
                    handleMeasurementUpdate={handleMeasurementUpdate} 
                />
            ) : (
                <DesktopView 
                    measurementData={measurementData} 
                    handleMeasurementUpdate={handleMeasurementUpdate} 
                />
            )}

            <AdditionalAttributes attributes={attributes} setAttributes={setAttributes} />
        </Box>
    );
}
